/**
 * Tile cases: what a terrain cell has to show, independent of how the neighbourhood was built.
 *
 * A case is the cell's own shape and which parts of its outline face the air: the halves of each side
 * that the neighbour across leaves open, plus corner points that touch air although both sides meeting
 * there are covered (inside corners). The cell's own slope always faces air.
 * Dirt (level layer) and grass (foreground layer) use the same cases, each with its own sprites:
 * key = layer letter ('d' / 'g') + own shape letter + 2 hex digits (2 bits per side L, R, B, T) +
 * 1 hex digit (corners BL, BR, TL, TR) + 2 hex digits per open corner: the surfaces meeting at the
 * corner point (cornerShape), so slopes there make their own cases. The 5-letter key without that detail
 * is the general case of those corners: its link applies to every corner shape not linked itself.
 * Two neighbourhoods with the same key get the same sprite; a case and its mirror image are different
 * cases. Cells without anything exposed have no case (interior fill / no grass).
 */
import { EMPTY, FULL, SHAPES, SHAPE_COUNT, insideShape, transformShape, type ShapeKind } from './shapes.ts';
import { NB } from './keys.ts';
import { LINE_S } from '../model/format.ts';

const FLIP_X = 1;

export type CaseLayer = 'dirt' | 'grass';
export type Directions = { s45: boolean; s21: boolean; s12: boolean };

const ch = (id: number) => String.fromCharCode(97 + id);
const hex = (v: number, n: number) => (v >>> 0).toString(16).padStart(n, '0');

const exposure = (sides: number[], corners: number) => hex(sides[0] | (sides[1] << 2) | (sides[2] << 4) | (sides[3] << 6), 2) + hex(corners, 1);
const LAYER_CH: Record<CaseLayer, string> = { dirt: 'd', grass: 'g' };
function makeKey(layer: CaseLayer, own: number, sides: number[], corners: number, air: number[] | null): string {
  let k = LAYER_CH[layer] + ch(own) + exposure(sides, corners);
  if (air) for (let c = 0; c < 4; c++) if ((corners >> c) & 1) k += hex(air[c], 2);
  return k;
}

// ------------------------------------------------------------------ outline exposure

/*
 * Every supported edge meets a cell side at its ends or its midpoint, so each side is exposed in
 * halves: bit 0 = lower / left half, bit 1 = upper / right half. Sides are tested at the quarter
 * points (exact), corner points just inside each touching cell.
 */
const Q = [0.25, 0.75];
const E = 0.02;

/** point on side `side` (L, R, B, T) of a cell at parameter t along it */
function sidePoint(side: number, t: number): [number, number] {
  return side === 0 ? [0, t] : side === 1 ? [1, t] : side === 2 ? [t, 0] : [t, 1];
}
/** side of the neighbour facing side `side` */
const FACING = [1, 0, 3, 2];

const covers = (s: number, side: number, t: number) => {
  const [x, y] = sidePoint(side, t);
  // just inside the cell, so a slope ending exactly on the side does not count
  const ix = x === 0 ? E : x === 1 ? 1 - E : x, iy = y === 0 ? E : y === 1 ? 1 - E : y;
  return insideShape(s, ix, iy);
};

/** sideBits[side][own][neighbour]: exposed halves of that side */
const sideBits: number[][][] = [0, 1, 2, 3].map((side) => {
  const out: number[][] = [];
  for (let own = 0; own < SHAPE_COUNT; own++) {
    const row: number[] = [];
    for (let n = 0; n < SHAPE_COUNT; n++) {
      let b = 0;
      Q.forEach((t, h) => {
        if (covers(own, side, t) && !covers(n, FACING[side], t)) b |= 1 << h;
      });
      row.push(b);
    }
    out.push(row);
  }
  return out;
});

/** corner points of a cell: BL, BR, TL, TR */
const CORNER_PT: [number, number][] = [[0, 0], [1, 0], [0, 1], [1, 1]];

/** does shape s have air right at its corner c (also a slope line ending in the corner)? */
function airAt(s: number, c: number): boolean {
  const [cx, cy] = CORNER_PT[c];
  const sx = cx ? -1 : 1, sy = cy ? -1 : 1;
  return !insideShape(s, cx + sx * E, cy + sy * 2 * E) || !insideShape(s, cx + sx * 2 * E, cy + sy * E);
}
const ownsCorner = (s: number, c: number) => {
  const [cx, cy] = CORNER_PT[c];
  return insideShape(s, cx ? 1 - E : E, cy ? 1 - E : E);
};

/*
 * Around corner c of the centre: the diagonal neighbour (NB 4 + c) and the two side neighbours, each
 * with the index of its own corner at that point. A corner is exposed when the centre owns it, the
 * adjacent half-sides are covered and any of the three cells has air at the point.
 */
const CORNER_CELLS: [number, number][][] = [
  [[4, 3], [0, 1], [2, 2]],
  [[5, 2], [1, 0], [2, 3]],
  [[6, 1], [0, 3], [3, 0]],
  [[7, 0], [1, 2], [3, 1]],
];
/** half-side bits ending at each corner: [side, half] pairs */
const CORNER_SIDES: [number, number, number, number][] = [[0, 0, 2, 0], [1, 0, 2, 1], [0, 1, 3, 0], [1, 1, 3, 1]];

const airAtCache: boolean[] = [];
for (let s = 0; s < SHAPE_COUNT; s++) for (let c = 0; c < 4; c++) airAtCache.push(airAt(s, c));
const ownsCornerCache: boolean[] = [];
for (let s = 0; s < SHAPE_COUNT; s++) for (let c = 0; c < 4; c++) ownsCornerCache.push(ownsCorner(s, c));

function cornerBits(own: number, nb: ArrayLike<number>, sides: number[]): number {
  let out = 0;
  for (let c = 0; c < 4; c++) {
    if (!ownsCornerCache[own * 4 + c]) continue;
    const [sa, ha, sb, hb] = CORNER_SIDES[c];
    if ((sides[sa] >> ha) & 1 || (sides[sb] >> hb) & 1) continue;
    if (CORNER_CELLS[c].some(([i, k]) => airAtCache[nb[i] * 4 + k])) out |= 1 << c;
  }
  return out;
}

/*
 * The air around an open inside corner: which directions from the corner point lead into air. Edges
 * through a cell corner can only run at multiples of 90° plus 0, atan(1/2), 45° or atan(2), so 16
 * sectors between those angles describe it exactly. Sector s lies in quadrant s >> 2 (counter-clockwise
 * from +x); the centre's own quadrant is never air.
 */
const EDGE_ANGLES = [0, Math.atan(0.5), Math.PI / 4, Math.atan(2), Math.PI / 2];
const SECTOR_ANGLE = Array.from({ length: 16 }, (_, s) => ((s >> 2) * Math.PI) / 2 + (EDGE_ANGLES[s & 3] + EDGE_ANGLES[(s & 3) + 1]) / 2);
/** sample radius: well inside the edges through the corner, short of any other edge (≥ 0.35 away) */
const R = 0.05;
const NB_INDEX = new Map(NB.map(([x, y], i) => [x + ',' + y, i]));

/** per corner c, sector s: the NB index of the cell the sector lies in (-1: the centre) and the sample point in it */
const SECTOR_AT: { i: number; x: number; y: number }[][] = CORNER_PT.map(([cx, cy]) =>
  SECTOR_ANGLE.map((a) => {
    const px = cx + R * Math.cos(a), py = cy + R * Math.sin(a);
    const dx = Math.floor(px), dy = Math.floor(py);
    return { i: dx || dy ? NB_INDEX.get(dx + ',' + dy)! : -1, x: px - dx, y: py - dy };
  }),
);
/** airSectors[c][i][shape]: the sectors of corner c that neighbour i with that shape leaves in air */
const airSectors: number[][][] = SECTOR_AT.map((secs) =>
  NB.map((_, i) => {
    const row: number[] = [];
    for (let sh = 0; sh < SHAPE_COUNT; sh++) {
      let m = 0;
      secs.forEach((p, s) => {
        if (p.i === i && !insideShape(sh, p.x, p.y)) m |= 1 << s;
      });
      row.push(m);
    }
    return row;
  }),
);

function cornerAir(c: number, nb: ArrayLike<number>): number {
  let m = 0;
  for (let i = 0; i < 8; i++) m |= airSectors[c][i][nb[i]];
  return m;
}

/** the quadrant of corner c that lies in the centre cell */
const OWN_QUADRANT = CORNER_PT.map(([cx, cy]) => (cx ? (cy ? 2 : 1) : cy ? 3 : 0));

/*
 * What a corner's tile shows of the air there: the two surfaces running into the corner point, i.e.
 * the first air met turning away from the centre's own quadrant counter-clockwise and clockwise, as the
 * number of sectors passed (0-11) on each side. Air further round does not touch the cell.
 */
function cornerShape(c: number, mask: number): number {
  const q = OWN_QUADRANT[c] * 4;
  let ccw = 15, cw = 15;
  for (let k = 0; k < 12; k++)
    if ((mask >> ((q + 4 + k) & 15)) & 1) {
      ccw = k;
      break;
    }
  for (let k = 0; k < 12; k++)
    if ((mask >> ((q + 15 - k) & 15)) & 1) {
      cw = k;
      break;
    }
  return (ccw << 4) | cw;
}
/** the same corner seen in the mirror: counter-clockwise and clockwise swap */
const mirrorShape = (v: number) => ((v & 15) << 4) | (v >> 4);

/** A surface running into a corner point: the cell owning its line (NB index, -1 the cell itself), that cell's corner index at the point and the line (LINE_* bit). */
export interface CornerSurface {
  cell: number;
  corner: number;
  line: number;
}

/** side bit (LINE_L, R, B, T) of the cell at offset a that faces the side-adjacent cell at offset b */
function facingLine(a: number, b: number): number {
  const [ax, ay] = a < 0 ? [0, 0] : NB[a], [bx, by] = b < 0 ? [0, 0] : NB[b];
  const dx = bx - ax, dy = by - ay;
  return dx === -1 && !dy ? 1 : dx === 1 && !dy ? 2 : !dx && dy === -1 ? 4 : !dx && dy === 1 ? 8 : 0;
}

/**
 * The two surfaces meeting at open inside corner c of a cell, counter-clockwise first as in cornerShape:
 * the edge between the last dirt and the first air met turning away from the cell's own quadrant.
 */
export function cornerSurfaces(c: number, own: number, nb: ArrayLike<number>): [CornerSurface, CornerSurface] | null {
  const mask = cornerAir(c, nb);
  const q = OWN_QUADRANT[c] * 4;
  const surface = (dirt: number, air: number): CornerSurface => {
    const a = SECTOR_AT[c][dirt].i, b = SECTOR_AT[c][air].i;
    const corner = a < 0 ? c : CORNER_CELLS[c].find(([i]) => i === a)![1];
    // the cell's own air is not in the mask: next to it the surface is its own slope
    const line = a === b || (a < 0 && own > FULL && linesAtCorner(own, c) & LINE_S) ? LINE_S : facingLine(a, b);
    return { cell: a, corner, line };
  };
  let ccw: CornerSurface | null = null, cw: CornerSurface | null = null;
  for (let k = 0; k < 12 && !ccw; k++) if ((mask >> ((q + 4 + k) & 15)) & 1) ccw = surface((q + 3 + k) & 15, (q + 4 + k) & 15);
  for (let k = 0; k < 12 && !cw; k++) if ((mask >> ((q + 15 - k) & 15)) & 1) cw = surface((q + 16 - k) & 15, (q + 15 - k) & 15);
  return ccw && cw ? [ccw, cw] : null;
}

// ------------------------------------------------------------------ outline lines (per-line grass)

/** The lines of a cell's outline that face air (LINE_* bits): the exposed sides and its own slope. */
export function exposedLines(own: number, nb: ArrayLike<number>): number {
  if (own === EMPTY) return 0;
  let m = own > FULL ? LINE_S : 0;
  for (let s = 0; s < 4; s++) if (sideBits[s][own][nb[s]]) m |= 1 << s;
  return m;
}

/** Exposed halves of each side, 2 bits per side L, R, B, T (bit 0 lower / left half) as in a case key. */
export function exposedHalves(own: number, nb: ArrayLike<number>): number {
  let v = 0;
  for (let s = 0; s < 4; s++) v |= sideBits[s][own][nb[s]] << (2 * s);
  return v;
}

/** Inside corners of a cell (BL, BR, TL, TR bits) as in its case key. */
export function insideCorners(own: number, nb: ArrayLike<number>): number {
  const sides = [0, 1, 2, 3].map((s) => sideBits[s][own][nb[s]]);
  return own === EMPTY ? 0 : cornerBits(own, nb, sides);
}

/** Lines (LINE_* bits) of a cell of shape s that run through its corner point c. */
export function linesAtCorner(s: number, c: number): number {
  if (s === EMPTY) return 0;
  const [sa, , sb] = CORNER_SIDES[c];
  let m = (1 << sa) | (1 << sb);
  if (s > FULL) {
    const sh = SHAPES[s];
    const [cx, cy] = CORNER_PT[c];
    if (sh.n[0] * cx + sh.n[1] * cy === sh.c) m |= LINE_S;
  }
  return m;
}

/** Around corner c of a cell: [NB index, that cell's own corner index at the point] of the three cells there. */
export const cornerCells = (c: number): readonly [number, number][] => CORNER_CELLS[c];

/** For each corner BL, BR, TL, TR: the two sides meeting there. */
const CORNER_SIDE_PAIR = [[0, 2], [1, 2], [0, 3], [1, 3]];

/**
 * The neighbourhood a cell's grass is chosen in when only some of its exposed lines carry grass: every bare
 * side gets a full neighbour (and the diagonals beside it where the terrain carries on), a bare slope makes
 * the cell itself full, and the inside corners in `dropCorners` are closed. The result looks to the case keys
 * and the learned pieces like terrain whose only open outline is the grassy lines. Returns the shape to use.
 */
export function grassView(own: number, nb: ArrayLike<number>, bare: number, dropCorners: number, out: number[]): number {
  for (let i = 0; i < 8; i++) out[i] = nb[i];
  const o = bare & LINE_S && own > FULL ? FULL : own;
  const faked = [false, false, false, false];
  for (let s = 0; s < 4; s++) {
    const wanted = sideBits[s][own][nb[s]] && !(bare & (1 << s));
    if (sideBits[s][o][out[s]] && !wanted) {
      out[s] = FULL;
      faked[s] = true;
    }
  }
  for (let c = 0; c < 4; c++) {
    const [a, b] = CORNER_SIDE_PAIR[c];
    if ((faked[a] && !sideBits[b][o][out[b]]) || (faked[b] && !sideBits[a][o][out[a]])) out[4 + c] = FULL;
  }
  const sides = [0, 1, 2, 3].map((s) => sideBits[s][o][out[s]]);
  const before = cornerBits(own, nb, [0, 1, 2, 3].map((s) => sideBits[s][own][nb[s]]));
  const after = cornerBits(o, out, sides);
  const close = (after & ~before) | (after & dropCorners);
  for (let c = 0; c < 4; c++) if ((close >> c) & 1) out[4 + c] = FULL;
  return o;
}

// ------------------------------------------------------------------ keys

const memo = new Map<number, string>();
const tmpSides = [0, 0, 0, 0];
const tmpAir = [0, 0, 0, 0];

function nbCode(own: number, nb: ArrayLike<number>): number {
  let k = own;
  for (let i = 0; i < 8; i++) k = k * SHAPE_COUNT + nb[i];
  return k;
}

/** grass-layer key of a cell ('' without a case) */
function computeKey(own: number, nb: ArrayLike<number>): string {
  for (let s = 0; s < 4; s++) tmpSides[s] = sideBits[s][own][nb[s]];
  const corners = cornerBits(own, nb, tmpSides);
  if (own === FULL && !corners && !tmpSides.some((v) => v)) return '';
  for (let c = 0; c < 4; c++) tmpAir[c] = (corners >> c) & 1 ? cornerShape(c, cornerAir(c, nb)) : 0;
  return makeKey('grass', own, tmpSides, corners, tmpAir);
}

/** Case key of a cell ('' when the cell has no case: empty, or interior without exposed outline). */
export function caseKey(layer: CaseLayer, own: number, nb: ArrayLike<number>): string {
  if (own === EMPTY) return '';
  const code = nbCode(own, nb);
  let k = memo.get(code);
  if (k === undefined) {
    k = computeKey(own, nb);
    if (memo.size > 200_000) memo.clear();
    memo.set(code, k);
  }
  return layer === 'grass' || !k ? k : 'd' + k.slice(1);
}

/** The same case on the other layer. */
export const onLayer = (key: string, layer: CaseLayer): string => (key ? LAYER_CH[layer] + key.slice(1) : key);

// ------------------------------------------------------------------ partly grassy cells

/*
 * A cell whose outline carries grass in part only (per-line grass) has a grass case of its own: the case
 * of its outline + '_' + its grassy lines (2 hex digits, LINE_* bits) + per open inside corner in key
 * order which of the two surfaces meeting there are grassy (1 counter-clockwise, 2 clockwise, 3 both).
 * A cell beside the end of a grassy run carries the run's end on that line: 'h' + per line L, R, B, T, S
 * the end the grass comes in at (2 bits per line, lower / left end first; 2 hex digits, 3 with the slope),
 * where no open corner there tells it.
 */
export function partialCase(key: string, grassy: number, corners: number[], halves = 0): string {
  return key + '_' + hex(grassy, 2) + corners.map((v) => v.toString(16)).join('') + (halves ? 'h' + hex(halves, halves > 0xff ? 3 : 2) : '');
}

const splitPartial = (key: string): [string, string] => {
  const i = key.indexOf('_');
  return i < 0 ? [key, ''] : [key.slice(0, i), key.slice(i)];
};

/** Is this the case of a partly grassy cell? */
export const isPartialCase = (key: string) => key.includes('_');

// ------------------------------------------------------------------ exact neighbours

/*
 * A case narrowed to one exact neighbourhood: case key + '#' + the 8 neighbour shapes (NB order, one
 * letter each). Its link wins over the case's own for cells with exactly these neighbours, so a tile can
 * tell apart slopes and walls that the case itself sums up as the same exposed outline.
 *
 * Solid cells with no outline facing the air (under the surface, e.g. below the bottom of a V between two
 * slopes) have no case: they are BURIED. They can only be linked for exact neighbours. Those whose 8
 * neighbours are all solid too (interior) look 2 cells out: their key adds the 16 shapes of the ring
 * around the 3×3 (NB2 order), so a band can be thickened under the surface. Deep fill (the whole 5×5
 * solid) has no key: it is the style's fill.
 */
export const BURIED = 'd' + ch(FULL) + '000';

/** the ring at distance 2 around a cell, row by row from the bottom */
export const NB2: [number, number][] = [];
for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) if (Math.max(Math.abs(dx), Math.abs(dy)) === 2) NB2.push([dx, dy]);

/** The neighbourhood a buried cell's exact key is made of: its 8 neighbours, plus the ring when those are all solid; null for deep fill. */
export function buriedNeighbours(nb: ArrayLike<number>, ring: () => ArrayLike<number>): number[] | null {
  const out = Array.from(nb);
  if (out.some((s) => s !== FULL)) return out;
  const r = Array.from(ring());
  return r.some((s) => s !== FULL) ? [...out, ...r] : null;
}

export const exactCase = (key: string, nb: ArrayLike<number>): string => {
  if (!key) return '';
  let k = key + '#';
  for (let i = 0; i < nb.length && i < 24; i++) k += ch(nb[i]);
  return k;
};

export const isExactCase = (key: string) => key.includes('#');

const splitExact = (key: string): [string, string] => {
  const i = key.indexOf('#');
  if (i < 0) return [key, ''];
  const rest = key.slice(i + 1);
  const tone = rest.indexOf('!');
  return [key.slice(0, i), tone < 0 ? rest : rest.slice(0, tone)];
};

/** The case an exact-neighbours case narrows (the key itself for any other case). */
export const baseCase = (key: string): string => splitExact(key)[0];

/** Neighbour shapes of an exact-neighbours case (NB order, then NB2 for buried interior cells), else null. */
export function exactNeighbours(key: string): number[] | null {
  const [, s] = splitExact(key);
  if (s.length !== 8 && s.length !== 24) return null;
  const nb = [...s].map((c) => c.charCodeAt(0) - 97);
  return nb.every((v) => v >= 0 && v < SHAPE_COUNT) ? nb : null;
}

const MIRROR_NB = [1, 0, 2, 3, 5, 4, 7, 6];
const MIRROR_NB2 = NB2.map(([dx, dy]) => NB2.findIndex(([x, y]) => x === -dx && y === dy));
const mirrorNeighbours = (nb: number[]) => [...MIRROR_NB.map((i) => nb[i]), ...(nb.length > 8 ? MIRROR_NB2.map((i) => nb[8 + i]) : [])].map((s) => transformShape(s, FLIP_X));

/*
 * The same neighbourhood plus which of those cells show light ground: exact key + '!' + hex bits, one per
 * neighbour (NB order, then NB2). A neighbour is light when it shows any tile that is not the style's plain
 * fill (a cobble band counts even where its last pixels go dark). Its link wins over the exact-neighbours
 * link, so light ground touching light ground can take a different tile from light ground over fill.
 */
export const isToneCase = (key: string) => key.includes('!');

export function toneMask(key: string): number | null {
  const i = key.indexOf('!');
  if (i < 0) return null;
  const hex = key.slice(i + 1);
  return /^[0-9a-f]+$/.test(hex) ? parseInt(hex, 16) : null;
}

export function toneCase(key: string, nb: ArrayLike<number>, mask: number): string {
  const bare = key.split('!')[0];
  const exact = bare.includes('#') ? bare : exactCase(bare, nb);
  const n = Math.min(nb.length, 24);
  return exact + '!' + (mask >>> 0).toString(16).padStart(n > 8 ? 6 : 2, '0');
}

const mirrorTone = (mask: number, n: number): number => {
  const src = n > 8 ? [...MIRROR_NB, ...MIRROR_NB2.map((i) => 8 + i)] : MIRROR_NB;
  let out = 0;
  for (let i = 0; i < n; i++) if ((mask >>> src[i]) & 1) out |= 1 << i;
  return out;
};

const swapLR = (lines: number) => (lines & ~3) | ((lines & 1) << 1) | ((lines >> 1) & 1);

// ------------------------------------------------------------------ decoding

export interface CaseInfo {
  layer: CaseLayer;
  own: number;
  /** exposed halves per side L, R, B, T (bit 0 lower / left half, bit 1 upper / right half) */
  sides: number[];
  /** inside corner bits BL, BR, TL, TR */
  corners: number;
  /** per corner: the surfaces meeting there (see cornerShape); null for a general case */
  air: number[] | null;
  /** partly grassy cell: its grassy lines (LINE_* bits) and per corner its grassy surfaces (see partialCase) */
  grass: { lines: number; corners: number[]; halves: number } | null;
}

const popcount = (v: number) => (v & 1) + ((v >> 1) & 1) + ((v >> 2) & 1) + ((v >> 3) & 1);

export function parseCase(full: string): CaseInfo | null {
  const [key, part] = splitPartial(baseCase(full));
  const own = key.charCodeAt(1) - 97;
  if (!(own > EMPTY && own < SHAPE_COUNT) || key.length < 5) return null;
  const layer = caseLayerOf(key);
  if (!layer) return null;
  const v = parseInt(key.slice(2, 4), 16);
  const corners = parseInt(key[4], 16);
  const n = popcount(corners);
  if (key.length !== 5 && key.length !== 5 + 2 * n) return null;
  let air: number[] | null = null;
  if (key.length > 5 || !n) {
    air = [0, 0, 0, 0];
    let at = 5;
    for (let c = 0; c < 4; c++) if ((corners >> c) & 1) air[c] = parseInt(key.slice(at, (at += 2)), 16);
  }
  let grass: CaseInfo['grass'] = null;
  if (part) {
    const hi = part.indexOf('h');
    if ((hi < 0 ? part.length : hi) !== 3 + n || (hi >= 0 && part.length !== hi + 3 && part.length !== hi + 4)) return null;
    const at = [0, 0, 0, 0];
    let i = 3;
    for (let c = 0; c < 4; c++) if ((corners >> c) & 1) at[c] = parseInt(part[i++], 16);
    grass = { lines: parseInt(part.slice(1, 3), 16), corners: at, halves: hi < 0 ? 0 : parseInt(part.slice(hi + 1), 16) };
  }
  return { layer, own, sides: [v & 3, (v >> 2) & 3, (v >> 4) & 3, (v >> 6) & 3], corners, air, grass };
}

/** The general case of a case with inside corners (any shape of air around them), else null. */
export const generalCase = (full: string): string | null => {
  if (isExactCase(full)) return null;
  const [key, part] = splitPartial(full);
  return key.length > 5 ? key.slice(0, 5) + part : null;
};

export const caseLayerOf = (key: string): CaseLayer | null => (key[0] === 'd' ? 'dirt' : key[0] === 'g' ? 'grass' : null);

const rev2 = (v: number) => ((v & 1) << 1) | ((v >> 1) & 1);

/** Key of the horizontally mirrored case. */
export function mirrorCase(key: string): string {
  const nb = exactNeighbours(key);
  if (nb) {
    const flipped = exactCase(mirrorCase(baseCase(key)), mirrorNeighbours(nb));
    const tone = toneMask(key);
    return tone === null ? flipped : toneCase(flipped, nb, mirrorTone(tone, nb.length));
  }
  const c = parseCase(key);
  if (!c) return key;
  const own = transformShape(c.own, FLIP_X);
  const [l, r, b, t] = c.sides;
  const k = c.corners;
  const sides = [r, l, rev2(b), rev2(t)];
  const corners = ((k >> 1) & 1) | ((k & 1) << 1) | (((k >> 3) & 1) << 2) | (((k >> 2) & 1) << 3);
  const air = c.air && [0, 1, 2, 3].map((q) => mirrorShape(c.air![q ^ 1]));
  const k2 = makeKey(c.layer, own, sides, corners, air);
  if (!c.grass) return k2;
  const g = c.grass;
  const open = [0, 1, 2, 3].filter((q) => (corners >> q) & 1);
  const hs = [0, 1, 2, 3, 4].map((s) => (g.halves >> (2 * s)) & 3);
  const halves = hs[1] | (hs[0] << 2) | (rev2(hs[2]) << 4) | (rev2(hs[3]) << 6) | (rev2(hs[4]) << 8);
  return partialCase(k2, swapLR(g.lines), open.map((q) => rev2(g.corners[q ^ 1])), halves);
}

// ------------------------------------------------------------------ enumeration

export interface CaseEntry {
  key: string;
  /** one neighbourhood producing the case (NB order) */
  nb: number[];
}

export function allowedShapes(dirs: Directions): number[] {
  const out: number[] = [];
  for (let s = 0; s < SHAPE_COUNT; s++) {
    const k: ShapeKind = SHAPES[s].kind;
    if (k === 'empty' || k === 'full' || dirs[k as keyof Directions]) out.push(s);
  }
  return out;
}

/**
 * Every case a style's shape classes can form in any 3×3 neighbourhood (most of them only with
 * contrived geometry), cases with inside corners as their general case (the shapes of air around the
 * corners multiply too much to list). Built cell by cell over the distinct partial results.
 */
export function enumerateCases(layer: CaseLayer, dirs: Directions): CaseEntry[] {
  const shapes = allowedShapes(dirs);
  const out: CaseEntry[] = [];
  const seen = new Set<string>();
  for (const own of shapes) {
    if (own === EMPTY) continue;
    // a: exposed half-sides (2 bits per side); b: corners with air at the point
    type St = { a: number; b: number; nb: number[] };
    let cur = new Map<string, St>([['', { a: 0, b: 0, nb: [] }]]);
    for (let i = 0; i < 8; i++) {
      const next = new Map<string, St>();
      for (const e of cur.values())
        for (const sh of shapes) {
          const a = i < 4 ? e.a | (sideBits[i][own][sh] << (2 * i)) : e.a;
          let b = e.b;
          for (let q = 0; q < 4; q++) for (const [ni, k] of CORNER_CELLS[q]) if (ni === i && airAtCache[sh * 4 + k]) b |= 1 << q;
          const k = a + ',' + b;
          if (!next.has(k)) next.set(k, { a, b, nb: [...e.nb, sh] });
        }
      cur = next;
    }
    for (const e of cur.values()) {
      const full = caseKey(layer, own, e.nb);
      const key = full && (generalCase(full) ?? full);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push({ key, nb: e.nb });
    }
  }
  return out;
}
